// Cloudflare Turnstile widget (ported from MyFootballJournal). When the site
// key is empty it renders nothing and onVerify never fires — the pre-launch
// path where the backend's TURNSTILE_SECRET is likewise empty (fail-open).
import { useEffect, useRef, type ReactElement } from 'react'

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
const SCRIPT_ID = 'hh-turnstile-script'

interface TurnstileGlobal {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string
      callback: (token: string) => void
      'error-callback'?: () => void
      'expired-callback'?: () => void
      theme?: 'light' | 'dark' | 'auto'
    },
  ) => string
}
declare global {
  interface Window {
    turnstile?: TurnstileGlobal
  }
}

interface Props {
  sitekey?: string
  onVerify: (token: string) => void
  onExpire?: () => void
  onError?: () => void
}

export function Turnstile({ sitekey, onVerify, onExpire, onError }: Props): ReactElement | null {
  const ref = useRef<HTMLDivElement | null>(null)
  const widgetId = useRef<string | null>(null)

  useEffect(() => {
    if (!sitekey || !ref.current) return
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script')
      s.id = SCRIPT_ID
      s.src = SCRIPT_URL
      s.async = true
      s.defer = true
      document.head.appendChild(s)
    }
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      const t = window.turnstile
      if (!t) return void setTimeout(tick, 50)
      if (widgetId.current) return
      widgetId.current = t.render(ref.current!, {
        sitekey,
        callback: onVerify,
        'expired-callback': onExpire,
        'error-callback': onError,
        theme: 'auto',
      })
    }
    tick()
    return () => {
      cancelled = true
    }
  }, [sitekey, onVerify, onExpire, onError])

  if (!sitekey) return null
  return <div ref={ref} className="my-3 flex justify-center" />
}
